import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { parseISO } from "date-fns";
import { Box, Typography, RadioGroup, FormControlLabel, FormControl, Divider } from "@mui/material";
import CityAutocomplete from "../../../Inputs/CityAutocomplete/CityAutocomplete";
import PrimaryDatePicker from "../../../Inputs/PrimaryDatePicker/PrimaryDatePicker";
import PrimaryTextField from "../../../Inputs/PrimaryTextField/PrimaryTextField";
import PrimaryRadio from "../../../Buttons/PrimaryRadio/PrimaryRadio";
import PrimaryButton from "../../../Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../../Buttons/SecondaryButton/SecondaryButton";
import useEquipmentTypes from "../../../../hooks/useEquipmentTypes/useEquipmentTypes";
import { getDistance } from "../../../../utils/calcHelpers";
import { handleAltEnter } from "../../../../utils/keyboardShortcutHelpers";
import { CarrierService } from "../../../../services/CarrierService";
import { AlertContext } from "../../../Dashboard/Dashboard";

export default function PostedTrucksManual({
  action,
  userIdMcld,
  activeCarrier,
  existingLane,
  handleClose
}) {
  let today = new Date();
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const { typeLabels } = useEquipmentTypes();
  const [originCity, setOriginCity] = useState({});
  const [destinationCity, setDestinationCity] = useState({});
  const [originCityDefault, setOriginCityDefault] = useState("");
  const [destinationCityDefault, setDestinationCityDefault] = useState("");
  const [startDate, setStartDate] = useState(new Date());
  const [expiryDate, setExpiryDate] = useState(new Date(today.setDate(today.getDate() + 1)));
  const [mode, setMode] = useState("V53");
  const [proposedCost, setProposedCost] = useState(null);
  const [proposedRpm, setProposedRpm] = useState(null);
  const [miles, setMiles] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);

  const handleReset = () => {
    setOriginCity({});
    setDestinationCity({});
    setStartDate(new Date());
    setExpiryDate(new Date(today.setDate(today.getDate() + 1)));
    setMode("V53");
    setProposedCost(null);
    setProposedRpm(null);
  };

  const handleCancel = () => {
    handleReset();
    handleClose();
  };

  const handleModeChange = (event) => {
    setMode(event.target.value);
  };

  const handleStartDateChange = (value) => {
    setStartDate(value);
    if (value) {
      let date = new Date(value.getTime());
      setExpiryDate(new Date(date.setDate(date.getDate() + 1)));
    } else {
      setExpiryDate(value);
    }
  };

  const handleSave = () => {
    setIsLoading(true);
    const payload = {
      carrierId: activeCarrier.id,
      originCity: originCity.name,
      originState: originCity.state,
      originCityId: originCity.id,
      destinationCity: destinationCity.name,
      destinationState: destinationCity.state,
      destinationCityId: destinationCity.id,
      startDate: startDate.toISOString(),
      expiryDate: expiryDate.toISOString(),
      postedDate: new Date().toISOString(),
      postedBy: userIdMcld,
      dispatcher: userIdMcld,
      equipmentTypeId: mode,
      proposedCost: proposedCost || 0,
      proposedRpm: proposedRpm || 0,
      miles: miles
    };

    if (action === "edit-capacity") {
      payload.id = existingLane.id;
      payload.updatedBy = userIdMcld;

      CarrierService.UpdatePostedTruck(instance, payload).then(() => {
        setIsLoading(false);
        handleClose();
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    } else {
      CarrierService.CreatePostedTruck(instance, payload).then(() => {
        setIsLoading(false);
        handleClose();
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    }
  };

  useEffect(() => {
    setSubmitDisabled(
      !activeCarrier ||
      !originCity ||
      !startDate ||
      !expiryDate
    );
  }, [
    activeCarrier,
    originCity,
    startDate,
    expiryDate
  ]);

  useEffect(() => {
    if (originCity?.id && destinationCity?.id) {
      setMiles(
        getDistance(
          originCity.latitude,
          originCity.longitude,
          destinationCity.latitude,
          destinationCity.longitude
        )
      );
    } else {
      setMiles(null);
    }
  }, [originCity, destinationCity]);

  useEffect(() => {
    if (existingLane) {
      setStartDate(parseISO(existingLane.startDate));
      setExpiryDate(existingLane.expiryDate && parseISO(existingLane.expiryDate));
      setMode(existingLane.equipmentTypeId);
      setProposedCost(existingLane.proposedCost && !existingLane.proposedRpm ? existingLane.proposedCost : null);
      setProposedRpm(existingLane.proposedRpm ? existingLane.proposedRpm : null);
      setMiles(existingLane.miles);
      setOriginCityDefault(existingLane.originCity ? `${existingLane.originCity}, ${existingLane.originState}` : "");
      setDestinationCityDefault(existingLane.destinationCity ? `${existingLane.destinationCity}, ${existingLane.destinationState}` : "");
    }
  }, [existingLane]);

  return (
    <Box onKeyDown={(e) => handleAltEnter(e, (submitDisabled || isLoading), handleSave)}>
      <Box className="Carrier-modal-input-row">
        <Typography variant="subhead2" color="black_solid.80">
          Locations
        </Typography>
      </Box>
      <Box className="Carrier-modal-input-row">
        <Box>
          <CityAutocomplete
            label="Current Location*"
            updateSelectedCity={setOriginCity}
            defaultInput={originCityDefault}
            error={!originCity}
          />
        </Box>
        <Box sx={{ marginLeft: "20px" }}>
          <CityAutocomplete
            label="Destination (optional)"
            updateSelectedCity={setDestinationCity}
            defaultInput={destinationCityDefault}
          />
        </Box>
      </Box>
      <Box className="Carrier-modal-input-row" sx={{ alignItems: "center" }}>
        <Typography variant="subhead2" color="black_solid.80">
          Mode
        </Typography>
        <FormControl sx={{ margin: "2px 0 0 10px" }}>
          <RadioGroup
            row
            aria-labelledby="carrier-search-radio-buttons"
            name="row-radio-buttons-group"
            value={mode}
            onChange={handleModeChange}
          >
            {typeLabels.map(type => (
              <FormControlLabel
                key={`equipment-type-${type.id}`}
                value={type.id}
                control={<PrimaryRadio />}
                label={<Typography variant="body2" color="black_solid.60">{type.label}</Typography>}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Box>
      <Divider sx={{ marginBottom: "16px" }} />
      <Box className="Carrier-modal-input-row">
        <Typography variant="subhead2" color="black_solid.80">
          Dates
        </Typography>
      </Box>
      <Box className="Carrier-modal-input-row">
        <Box>
          <PrimaryDatePicker
            label="Start Date *"
            value={startDate}
            handleChange={handleStartDateChange}
            width="260px"
            disablePast
            error={!startDate}
          />
        </Box>
        <Box sx={{ marginLeft: "20px" }}>
          <PrimaryDatePicker
            label="Expiry Date *"
            value={expiryDate}
            handleChange={setExpiryDate}
            width="260px"
            disablePast
            error={!expiryDate}
          />
        </Box>
      </Box>
      <Divider sx={{ marginBottom: "16px" }} />
      <Box className="Carrier-modal-input-row">
        <Typography variant="subhead2" color="black_solid.80">
          Costs
        </Typography>
      </Box>
      <Box className="Carrier-modal-input-row">
        <Box>
          <PrimaryTextField
            onChange={setProposedCost}
            value={proposedCost}
            label="Proposed Cost"
            isNumber
            isCurrency
            width="200px"
            disabled={!!proposedRpm}
          />
        </Box>
        <Box sx={{ marginLeft: "20px" }}>
          <PrimaryTextField
            onChange={setProposedRpm}
            value={proposedRpm}
            label="Rate per Mile"
            isNumber
            isCurrency
            width="200px"
            disabled={!!proposedCost}
          />
        </Box>
      </Box>
      <Box sx={{ display: "flex" }}>
        <Box sx={{ marginLeft: "auto", marginRight: "20px" }}>
          <SecondaryButton
            label="Cancel"
            onClick={handleCancel}
            disabled={isLoading}
          />
        </Box>
        <Box>
          <PrimaryButton
            label={action === "edit-capacity" ? "Update" : "Post"}
            width="60px"
            onClick={handleSave}
            isLoading={isLoading}
            disabled={submitDisabled || isLoading}
          />
        </Box>
      </Box>
    </Box>
  );
}

PostedTrucksManual.propTypes = {
  action: PropTypes.string,
  userIdMcld: PropTypes.string,
  activeCarrier: PropTypes.object,
  existingLane: PropTypes.object,
  handleClose: PropTypes.func.isRequired
};